import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";

import { Header, Icon, Image } from "@onmo/ui";
import { HtmlGameView } from "@onmo/html-game";
import {
  CasualHtmlLaunchData,
  endGamePlayGql,
  EndGamePlayOutput,
  GamePlayMode,
  GameSessionInputStatus,
  GameType,
  globalLeaderboardsGQL,
  GlobalLeaderboardType,
  joinGamePlayGQL,
  JoinGamePlayOutput,
  Mutation,
} from "@onmo/graphql/graphql";
import {
  HtmlGameData,
  useInitializeGame,
  HtmlGameCallbacks,
  OnmoHtmlGame,
  ExternalGameMessage,
} from "@onmo/html-game";

import LogoIcon from "../../assets/image/logo_1x.webp";
import LogoIcon2x from "../../assets/image/logo_2x.webp";
import LogoIcon3x from "../../assets/image/logo_3x.webp";
import { useStyles } from "./GamePlay.style";
import GameLosePopup from "../../components/common/GameLosePopup/GameLosePopup";
import { useAppDispatch } from "../../hooks";
import { addPopupQueue } from "../../slices/popup";
import { PopupType } from "../../constants/popup";
import OurOfGems from "../../components/common/OutOfGems/OutOfGems";
import ResultScreen from "../../components/common/ResultScreen/ResultScreen";
import { PageRoutes } from "../../constants/routes";
import { usePurchaseRevive } from "../../hooks/resolvers/useShopMutation";
import { getTimeDuration } from "../../models/time";
import { useFetchReviveInventory } from "../../hooks/resolvers/useShopQuery";
import { GameLoading } from "../../components/common/GameLoading";
import { useFetchGames } from "../../hooks/useFetchGames";
import { getImage3x } from "../../models/theme";
import QuitGamePopup from "../../components/common/QuitGamePopup/QuitGamePopup";
import { showSnackbar } from "../../slices/alert";
import { useRightRotation } from "../../hooks/useRightRotation";
import { RotateScreen } from "../../components/common/RotateScreen";
import { GAME_IFRAME_MESSAGE } from "../../constants/game";

const GamePlay: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { gameId } = useParams<{ gameId: string }>();
  const [joinGamePlayMutation] = useMutation<Mutation>(joinGamePlayGQL);
  const [endGamePlayMutation] = useMutation<Mutation>(endGamePlayGql, {
    refetchQueries: [
      {
        query: globalLeaderboardsGQL,
        variables: {
          input: { globalLeaderboardType: GlobalLeaderboardType.Daily },
        },
      },
    ],
  });
  const purchaseRevive = usePurchaseRevive();
  const { reviveInventoryAvailable } = useFetchReviveInventory();
  const [gamePlayData, setGamePlayData] = useState<JoinGamePlayOutput>();
  const [gameResultData, setGameResultData] = useState<EndGamePlayOutput>();
  const [isLoaded, setLoaded] = useState(false);
  const [isOpenReward, setOpenReward] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [score, setScore] = useState(0);
  const [endGameMessage, setEndGameMessage] = useState<ExternalGameMessage>();
  const { getGameImageById } = useFetchGames({ fetchPolicy: "cache-first" });
  const logoHeader = getImage3x("header-logo.webp");
  const isRightRotation = useRightRotation(
    gamePlayData?.gameSession?.game?.orientation,
  );

  const onClickClose = () => {
    dispatch(addPopupQueue({ type: PopupType.QuitGame }));
    gameController?.pause();
  };

  const htmlGameData: HtmlGameData | null = useMemo(() => {
    if (!gamePlayData || !!gameResultData) return null;
    const currentGameData = gamePlayData.gameSession.game;
    if (!currentGameData) return null;

    const launchData = gamePlayData.gameSession
      .launchData as CasualHtmlLaunchData;
    if (currentGameData.type === GameType.IframeHtml) {
      return {
        type: GameType.IframeHtml,
        url: launchData?.launchUrl,
      };
    } else {
      return {
        type: GameType.DirectHtml,
        bundleURL: launchData?.launchUrl,
        listMomentData: JSON.stringify(launchData?.gameConfig?.stages),
        isFullGameMode: true,
      };
    }
  }, [gamePlayData, gameResultData]);

  const callbacks: HtmlGameCallbacks = useMemo(() => {
    const handleEndGame = (_controller: OnmoHtmlGame, score?: number) => {
      console.log("Game ended with score:", score);
    };

    const handleScore = (_controller: OnmoHtmlGame, score?: number) => {
      setScore(score || 0);
    };

    const handleRevive = (message: ExternalGameMessage) => {
      setEndGameMessage(message);
      dispatch(addPopupQueue({ type: PopupType.GameLose }));
    };

    const handleHtmlGameLoaded = (_controller: OnmoHtmlGame) => {
      setLoaded(true);
    };

    return {
      onEndGame: handleEndGame,
      onHtmlGameLoaded: handleHtmlGameLoaded,
      onScore: handleScore,
      onBundleLoadingProgress: () => {},
      onClickRevive: handleRevive,
    };
  }, [dispatch, gameId]);

  const onDismissRevive = () => {
    const message = {
      response_id: endGameMessage?.request,
      status: GAME_IFRAME_MESSAGE.STATUS.DISMISSED,
      message: GAME_IFRAME_MESSAGE.MESSAGE.DISMISSED,
    };
    gameController?.postMessageToFrame?.(message);
  };

  const onPurchaseRevive = () => {
    if (!reviveInventoryAvailable) return;
    purchaseRevive(reviveInventoryAvailable);
    const message = {
      response_id: endGameMessage?.request,
      status: GAME_IFRAME_MESSAGE.STATUS.SUCCESS,
      message: GAME_IFRAME_MESSAGE.MESSAGE.VIEWED,
      callback_func: endGameMessage?.payload.callback_func,
    };
    gameController?.postMessageToFrame?.(message);
  };

  const gameController = useInitializeGame({
    gameData: htmlGameData || undefined,
    callbacks,
  });

  const onEndGamePlay = async () => {
    try {
      const { data } = await endGamePlayMutation({
        variables: {
          input: {
            gameSessionId: gamePlayData?.gameSession.id,
            status: GameSessionInputStatus.Completed,
            score: score,
            time: getTimeDuration(startTime),
          },
        },
      });
      setGameResultData(data?.endGamePlay);
    } catch (error) {
      console.error("something error when end game play", error);
      dispatch(
        showSnackbar({
          message: "⚠️ GamePlay End Resolver Failed.",
        }),
      );
    } finally {
      gameController?.unload();
      gameController?.hideCanvas();
      setOpenReward(true);
    }
  };

  const handleCloseEndscreen = () => setOpenReward(false);

  useEffect(() => {
    const startGame = async () => {
      if (!gameId) return;
      try {
        const { data } = await joinGamePlayMutation({
          variables: {
            input: {
              gameId,
              mode: GamePlayMode.CasualHtml,
              startImmediately: true,
              modeData: {
                casualHtmlConfig: {},
              },
            },
          },
        });
        if (data?.joinGamePlay) {
          setStartTime(Date.now());
          setGamePlayData(data.joinGamePlay);
        }
      } catch (err) {
        console.error("Error starting game:", err);
        dispatch(addPopupQueue({ type: PopupType.OutOfGems }));
      }
    };

    startGame();
  }, [gameId, joinGamePlayMutation, dispatch]);

  useEffect(() => {
    return () => {
      setGamePlayData(undefined);
      setGameResultData(undefined);
      setLoaded(false);
      setOpenReward(false);
      setScore(0);
      setEndGameMessage(undefined);
      // reset style set from game canvas
      document.documentElement.style.overflow = "";
      document.documentElement.classList = "";
    };
  }, []);

  return (
    <div className={classes.root}>
      <Header
        title={
          <Image
            src={logoHeader?.oneX || LogoIcon}
            src2x={logoHeader?.twoX || LogoIcon2x}
            src3x={logoHeader?.threeX || LogoIcon3x}
            alt="logo"
          />
        }
        leftElement={<Icon name="icon-bold-close" onClick={onClickClose} />}
      />
      <div id="game-container" className={classes.gameBox}>
        {!isOpenReward && <HtmlGameView />}
      </div>
      {isLoaded && !isRightRotation && !isOpenReward && <RotateScreen />}
      {!isLoaded && (
        <GameLoading gameImageUrl={getGameImageById(gameId)?.webp3x || ""} />
      )}
      {reviveInventoryAvailable && (
        <GameLosePopup
          onCancel={onDismissRevive}
          onContinue={onPurchaseRevive}
        />
      )}
      <QuitGamePopup
        onExit={onEndGamePlay}
        onResumeGame={() => gameController?.resume?.()}
      />
      <OurOfGems onClose={() => navigate(PageRoutes.Home, { replace: true })} />
      {isOpenReward && (
        <ResultScreen
          reward={gameResultData?.gameParticipant.prize?.[0]?.amount || 0}
          onCloseEndscreen={handleCloseEndscreen}
        />
      )}
    </div>
  );
};

export default GamePlay;
